import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { User } from '~/models/user.model';
import { toSHA256 } from '~/utils/crypto.utils';
import { IAnalyticsService } from '~/services/interfaces/IAnalyticsService';
import { daysDifference } from '~/utils/date.utils';

dayjs.extend(utc);

export class GoogleTagManagerService {
    targetEventVerticals: string[];
    targetNonEventVerticals: string[];
    analyticsService: IAnalyticsService;

    constructor(analyticsService: IAnalyticsService) {
        this.analyticsService = analyticsService;
        this.targetEventVerticals = [
            'officiant',
            'photographer',
            'catering',
            'cinematography',
            'event_planner',
            'stationery',
            'florist',
            'rental',
            'venue',
            'calligrapher'
        ];
        this.targetNonEventVerticals = [
            'virtual_assistant',
            'home_staging',
            'interior_designer',
            'doula',
            'web_designer',
            'coaching',
            'agency',
            'graphic_designer',
            'marketing',
            'other',
            'social_media',
            'freelance',
            'business_consultant'
        ];
    }

    static getEnhancedConversionData = user => {
        const ecData: Record<string, any> = {};
        if (user) {
            // need to format to E.164 format, leaving + and numbers only
            const phone_number = `+1${user.phone_number.replace(/[^\d]/g, '')}`;
            const email = user.email.toLowerCase();
            ecData.email = toSHA256({ str: email });
            ecData.phone_number = toSHA256({ str: phone_number });
        }

        return ecData;
    };

    pushEvent(
        user: User,
        event: GtmAnalyticsEvents,
        args: Record<string, any> = {}
    ) {
        args.ecData = GoogleTagManagerService.getEnhancedConversionData(user);
        window.dataLayer ||= [];
        window.dataLayer.push({
            event,
            ...args
        });
        this.analyticsService.track('gtm_tracking_event', {
            ...args,
            gtm_event: event
        });
    }

    trackEvent(
        user: User,
        event: GtmAnalyticsEvents,
        args?: Record<string, any>
    ): void {
        this.pushEvent(user, event, {
            page_url: window.location.href,
            ...args
        });
        // eslint-disable-next-line no-console
        console.debug(
            `[tracking: GoogleTagManager] ${event}: ${JSON.stringify(args)}`
        );
    }

    onStartTrial(user: User) {
        const vertical = user.company.company_type;

        const pushTrialEvent = eventName => {
            this.pushEvent(user, eventName, {
                order_id: user.attribution?.external_id,
                user_id: user._id,
                sha256_user_email: toSHA256({
                    str: user.email
                }),
                vertical: user.company.company_type_name
            });
        };

        pushTrialEvent('start_trial');

        if (this.targetEventVerticals.includes(vertical)) {
            pushTrialEvent('tev_start_trial');
        }

        if (this.targetNonEventVerticals.includes(vertical)) {
            pushTrialEvent('tnev_start_trial');
        }

        this.pushEvent(user, GtmAnalyticsEvents.FormSubmitted);
    }

    onStartTrialClicked(user) {
        this.pushEvent(user, GtmAnalyticsEvents.GTMClick, {
            'gtm.elementId': 'intakeFormSubmit'
        });
    }

    onProfileImageUpload(user) {
        const now = new Date();
        const trialStartedDate = user?.account?.trial?.start_date;

        if (daysDifference(now, trialStartedDate) < 1) {
            this.pushEvent(user, GtmAnalyticsEvents.ProfileImageUploaded, {
                event: 'profile_image_upload',
                user_id: user._id,
                sha256_user_email: toSHA256({
                    str: user.email
                }),
                profile_image_upload_date: now,
                trial_started_date: trialStartedDate
            });
            this.analyticsService.track('Profile Image Uploaded', { user });
        }
    }

    onCompanyIconUpload(user: User) {
        const now = new Date();
        const trialStartedDate = user.getTrialStartDate();

        if (!trialStartedDate) {
            return;
        }

        if (daysDifference(now, trialStartedDate) < 1) {
            this.pushEvent(user, GtmAnalyticsEvents.CompanyIconUpload, {
                event: 'company_icon_upload',
                user_id: user._id,
                sha256_user_email: toSHA256({ str: user.email }),
                company_icon_upload_date: now,
                trial_started_date: trialStartedDate
            });

            this.analyticsService.track('Company Icon Uploaded', { user });
        }
    }
}

export enum GtmAnalyticsEvents {
    SignupSuccess = 'signup_success',
    UserHasPurchasedSubscription = 'subscribe',
    SubscriptionCreated = 'subscription_created',
    StartTrial = 'Start Trial',
    TevStartTrial = 'TEV Start Trial',
    TnevStartTrial = 'TNEV Start Trial',
    PurchaseSubscription = 'purchaseSubscription',
    GTMClick = 'gtm.click',
    FormSubmitted = 'formSubmitted',
    ProfileImageUploaded = 'profile_image_upload',
    CompanyIconUpload = 'company_icon_upload'
}
